/**
 * Teleport helps resolving z-index issues of overlapping popup elements.
 * Initiate it in your component's connectedCallback.
 * Call activate() to teleport the object.
 * Call deactivate() to resume the object to its original place.
 * Call observeDeletion() to activate deleteionObserver.
 * Call makeSiblingsInert() to add inert attribute to sibling elements.
 * Call removeInertFromSiblings() to remove inert attribute from sibling elements.
 */

export class Teleport {
  element: HTMLElement
  target: HTMLElement
  stub: HTMLElement
  teleportId: string
  deletionObserver: MutationObserver
  get isActive() {
    return this._isTeleportSkipped() || this.element.parentElement === this.target
  }

  constructor(element: HTMLElement, target?: HTMLElement) {
    this.element = element
    this.target = target || document.body
    this.teleportId = `${Math.random()}-${Date.now()}`.substring(2)
  }

  activate() {
    if (this._isTeleportSkipped() || this.isActive) {
      return
    }
    this._activate()
  }

  deactivate() {
    if (this._isTeleportSkipped() || !this.isActive) {
      return
    }
    this._deactivate()
  }

  observeDeletion() {
    if (this._isTeleportSkipped()) {
      return
    }
    this._observeDeletion()
  }

  makeSiblingsInert() {
    if (this._isTeleportSkipped() || !this.isActive) {
      return
    }
    this._makeSiblingsInert()
  }

  removeInertFromSiblings(delay: number = 0) {
    if (this._isTeleportSkipped() || !this.isActive) {
      return
    }
    if (delay) {
      setTimeout(this._removeInertFromSiblings.bind(this), delay)
    } else {
      this._removeInertFromSiblings()
    }
  }

  private _isTeleportSkipped() {
    const skipTeleport = this.element.getAttribute("skip-teleport")
    return skipTeleport !== null && skipTeleport !== "false"
  }

  private _observeDeletion() {
    if (this.deletionObserver) {
      this.deletionObserver.disconnect()
    }
    this.deletionObserver = new MutationObserver(this._deletionObserverCallback.bind(this))
    this.deletionObserver.observe(document.documentElement, { childList: true, subtree: true })
  }

  private _activate() {
    if (!this.element.dataset.reactPortal) {
      if (this.stub) {
        this.stub.remove()
      }
      this.stub = document.createElement("template")
      this.element.insertAdjacentElement("afterend", this.stub)
      this.target.appendChild(this.element)
    }
  }

  private _deactivate() {
    if (!this.element.dataset.reactPortal) {
      this.stub.insertAdjacentElement("beforebegin", this.element)
      this.stub.remove()
    }
    this._removeInertFromSiblings()
    this.deletionObserver.disconnect()
  }

  private _remove() {
    if (!this.element.dataset.reactPortal) {
      this.element?.remove()
      this.stub?.remove()
    }
    this._removeInertFromSiblings()
    this.deletionObserver.disconnect()
  }

  private readonly skipInert = ["duet-tooltip-popup", ".duet-skip-teleport-inert"]
  private _makeSiblingsInert() {
    const siblings = Array.from(this.target.children) as HTMLElement[]
    siblings.forEach(sibling => {
      if (sibling !== this.element && !this.skipInert.some(selector => sibling.matches(selector))) {
        sibling.dataset.duetTeleportId = (sibling.dataset.duetTeleportId || "") + this.teleportId
        sibling.inert = true
      }
    })
  }

  private _removeInertFromSiblings() {
    const siblings = Array.from(this.target.children) as HTMLElement[]
    siblings.forEach(sibling => {
      if (sibling.dataset.duetTeleportId?.includes(this.teleportId)) {
        sibling.dataset.duetTeleportId = sibling.dataset.duetTeleportId.replace(this.teleportId, "")
        if (sibling.dataset.duetTeleportId === "") {
          delete sibling.dataset.duetTeleportId
          sibling.inert = false
        }
      }
    })
  }

  /**
   * If the teleported element or its component (observed via stub) is removed from DOM, clean up the teleport.
   */
  private _deletionObserverCallback(mutationList: MutationRecord[]) {
    mutationList.forEach(mutation => {
      if (
        Array.from(mutation.removedNodes).includes(this.element) ||
        Array.from(mutation.removedNodes).some(n => n.contains(this.stub))
      ) {
        this._remove()
      }
    })
  }
}
